import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  BarChart3,
  Database,
  Zap,
  ShieldAlert,
  RefreshCw,
  Gauge,
  TrendingUp,
  Clock,
  Loader2,
  DownloadCloud,
  CheckCircle2,
  Music2,
  ListChecks,
  Users,
} from "lucide-react";
import { fetchYoutubeStats, searchYouTube, fetchSettings, updateSettings } from "../../lib/api";
import { KARAOKE_BATCHES } from "../../lib/karaokeBatches";
import CachedSongsDialog from "./CachedSongsDialog";
import { toast } from "sonner";

const StatCard = ({ icon: Icon, label, value, sub, accent = "primary", testid }) => (
  <div
    className="rounded-lg border border-border bg-surface-elevated/40 p-3 flex flex-col gap-1"
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
      <Icon className={`w-3.5 h-3.5 ${accent === "secondary" ? "text-secondary" : "text-primary"}`} />
      {label}
    </div>
    <p className={`font-display text-2xl font-black leading-none ${accent === "secondary" ? "text-secondary" : "text-foreground"}`}>
      {value}
    </p>
    {sub && <p className="text-[10px] text-muted-foreground/70 font-mono">{sub}</p>}
  </div>
);

const QuotaStatsDialog = ({ open, onOpenChange, onAddSong, onPlayNow }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showCachedSongs, setShowCachedSongs] = useState(false);
  const [guestYoutube, setGuestYoutube] = useState(true);
  const [savingSetting, setSavingSetting] = useState(false);

  // Cache warm-up by batch
  const [batchIdx, setBatchIdx] = useState(0);
  const [warming, setWarming] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [warmReport, setWarmReport] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchYoutubeStats();
      setStats(data);
    } catch (e) {
      console.error("Failed to load YouTube stats", e);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const runBatch = async () => {
    const songs = KARAOKE_BATCHES[batchIdx] || [];
    if (!songs.length) return;
    setWarming(true);
    setWarmReport(null);
    setProgress({ done: 0, total: songs.length });
    const t0 = Date.now();
    let cached = 0;
    let empty = 0;
    let failed = 0;
    let deleted = 0;
    let priv = 0;
    let misses = 0;
    for (let i = 0; i < songs.length; i++) {
      try {
        const data = await searchYouTube(songs[i], { maxResults: 12, confirmed: true });
        const n = (data.results || []).length;
        if (!data.fromCache) misses += 1;
        if (n >= 1) cached += 1;
        else empty += 1;
        const d = data.diagnostics || {};
        deleted += d.deleted_or_unavailable || 0;
        priv += d.private_or_non_embeddable || 0;
      } catch (e) {
        failed += 1;
      }
      setProgress({ done: i + 1, total: songs.length });
      // Throttle to stay under YouTube's per-100s rate limit (avoids 429s)
      if (i < songs.length - 1) await new Promise((r) => setTimeout(r, 400));
    }
    const elapsed = Math.round((Date.now() - t0) / 1000);
    setWarmReport({
      batch: batchIdx + 1,
      total: songs.length,
      cached,
      empty,
      failed,
      deleted,
      priv,
      elapsed,
      quota: misses * 101,
    });
    setWarming(false);
    toast.success(`Batch ${batchIdx + 1} pre-cargado`, {
      description: `${cached} cacheadas · ${misses * 101} unidades de cuota usadas`,
    });
    load();
  };

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (open) {
      fetchSettings().then((s) => setGuestYoutube(s.guest_youtube_search)).catch(() => {});
    }
  }, [open]);

  const toggleGuestYoutube = async () => {
    const next = !guestYoutube;
    setGuestYoutube(next); // optimistic + immediate effect
    setSavingSetting(true);
    try {
      await updateSettings(next);
      toast.success(next ? "Invitados pueden buscar en YouTube" : "Invitados limitados al catálogo del evento");
    } catch (e) {
      setGuestYoutube(!next);
      toast.error("No se pudo cambiar el ajuste");
    } finally {
      setSavingSetting(false);
    }
  };

  const hitRate = stats?.cache_hit_rate ?? 0;
  const cap = stats?.capacity || {};

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="quota-stats-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-secondary" />
                Cuota de YouTube
              </DialogTitle>
              <DialogDescription>
                Salud del caché y consumo de la API de YouTube.
              </DialogDescription>
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="refresh-stats-button"
              className="shrink-0 flex items-center gap-1.5 px-3 h-9 rounded-md border border-border hover:border-secondary hover:text-secondary transition-colors text-xs font-medium disabled:opacity-50"
              title="Actualizar"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Actualizar
            </button>
          </div>
        </DialogHeader>

        {/* Guest YouTube Search toggle (afecta solo a invitados QR; la consola del DJ no cambia) */}
        <div
          className={`rounded-lg border p-4 flex items-center justify-between gap-4 ${
            guestYoutube ? "border-green-500/40 bg-green-500/[0.06]" : "border-red-500/40 bg-red-500/[0.07]"
          }`}
          data-testid="guest-youtube-toggle-card"
        >
          <div className="min-w-0">
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Guest YouTube Search
            </p>
            <p className={`text-sm font-bold flex items-center gap-2 ${guestYoutube ? "text-green-400" : "text-red-400"}`} data-testid="guest-youtube-status">
              <span className={`w-2.5 h-2.5 rounded-full ${guestYoutube ? "bg-green-400" : "bg-red-400"} ${guestYoutube ? "animate-pulse" : ""}`} />
              {guestYoutube ? "Invitados pueden buscar en YouTube" : "Invitados limitados al catálogo del evento"}
            </p>
          </div>
          <button
            onClick={toggleGuestYoutube}
            disabled={savingSetting}
            data-testid="guest-youtube-toggle"
            role="switch"
            aria-checked={guestYoutube}
            title="Activar/desactivar búsqueda en YouTube para invitados"
            className={`relative shrink-0 w-14 h-8 rounded-full transition-colors disabled:opacity-60 ${
              guestYoutube ? "bg-green-500" : "bg-red-500/70"
            }`}
          >
            <span
              className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${
                guestYoutube ? "translate-x-7" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {error && (
          <div className="py-8 text-center text-destructive text-sm" data-testid="stats-error">
            No se pudieron cargar las estadísticas.
          </div>
        )}

        {!error && !stats && loading && (
          <div className="py-10 flex justify-center text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}

        {!error && stats && (
          <div className="space-y-4">
            {/* Hit rate hero */}
            <div className="rounded-lg border border-secondary/30 bg-secondary/[0.06] p-4 flex items-center justify-between">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-secondary/80 mb-1">
                  Tasa de aciertos de caché
                </p>
                <p className="font-display text-5xl font-black text-secondary leading-none" data-testid="stat-hit-rate">
                  {hitRate}%
                </p>
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                  {stats.total_searches} búsquedas totales
                </p>
              </div>
              <Gauge className="w-16 h-16 text-secondary/30" strokeWidth={1.2} />
            </div>

            {/* Core counters */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatCard icon={Database} label="Aciertos" value={stats.cache_hits} accent="secondary" testid="stat-hits" />
              <StatCard icon={Zap} label="Fallos" value={stats.cache_misses} testid="stat-misses" />
              <StatCard
                icon={TrendingUp}
                label="Cuota ahorrada"
                value={stats.estimated_quota_saved.toLocaleString("es")}
                sub="unidades"
                accent="secondary"
                testid="stat-quota-saved"
              />
              <StatCard
                icon={Zap}
                label="Cuota usada"
                value={stats.estimated_quota_consumed.toLocaleString("es")}
                sub="unidades"
                testid="stat-quota-consumed"
              />
            </div>

            {/* Secondary info */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <StatCard
                icon={ShieldAlert}
                label="Videos inválidos"
                value={stats.invalid_videos_detected}
                sub="bloqueados"
                testid="stat-invalid"
              />
              <StatCard
                icon={Clock}
                label="TTL caché"
                value={`${stats.current_cache_ttl_days}d`}
                sub="días de validez"
                accent="secondary"
                testid="stat-ttl"
              />
              <StatCard
                icon={ShieldAlert}
                label="Cuota agotada"
                value={stats.quota_exceeded_events ?? 0}
                sub="eventos"
                testid="stat-quota-exceeded"
              />
            </div>

            {/* Unique cached songs */}
            <div className="rounded-lg border border-primary/30 bg-primary/[0.06] p-4 flex items-center justify-between" data-testid="unique-songs-card">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-primary/80 mb-1 flex items-center gap-1.5">
                  <Music2 className="w-3.5 h-3.5" /> Canciones únicas cacheadas
                </p>
                <p className="font-display text-4xl font-black text-foreground leading-none" data-testid="stat-unique-songs">
                  {stats.unique_cached_songs_count ?? "—"}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1 font-mono">
                  agrupando versiones · {stats.temporary_cache_count ?? 0} búsquedas en caché
                </p>
              </div>
              <button
                onClick={() => setShowCachedSongs(true)}
                data-testid="view-cached-songs-button"
                className="shrink-0 flex items-center gap-1.5 px-4 h-10 rounded-md bg-primary text-primary-foreground font-bold text-xs uppercase tracking-wide hover:bg-primary/90 active:scale-95 transition-all glow-pink"
              >
                <ListChecks className="w-4 h-4" /> Ver lista
              </button>
            </div>

            {/* Capacity estimation */}
            <div className="rounded-lg border border-border bg-surface-elevated/40 p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
                <Gauge className="w-3.5 h-3.5 text-primary" />
                Capacidad estimada (cuota diaria {cap.daily_quota?.toLocaleString("es")})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <p className="font-display text-xl font-bold text-primary" data-testid="cap-current">
                    {cap.estimated_searches_per_day_at_current_hit_rate}
                  </p>
                  <p className="text-[11px] text-muted-foreground">búsquedas/día al ritmo actual</p>
                </div>
                <div>
                  <p className="font-display text-xl font-bold">{cap.max_new_searches_per_day}</p>
                  <p className="text-[11px] text-muted-foreground">búsquedas nuevas máx. (sin caché)</p>
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-secondary">{cap.cached_searches_per_day}</p>
                  <p className="text-[11px] text-muted-foreground">búsquedas cacheadas</p>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground/60 font-mono mt-3">
                Costo por búsqueda nueva: {cap.cost_per_search} unidades (search.list 100 + videos.list 1)
              </p>
            </div>

            {/* Cache warm-up by batch */}
            <div className="rounded-lg border border-border bg-surface-elevated/40 p-4">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-1.5">
                <DownloadCloud className="w-3.5 h-3.5 text-secondary" />
                Pre-cargar caché por batch
              </p>
              <div className="flex items-center gap-2">
                <select
                  value={batchIdx}
                  onChange={(e) => setBatchIdx(Number(e.target.value))}
                  disabled={warming}
                  data-testid="batch-select"
                  className="flex-1 bg-white/[0.05] border border-white/15 rounded-md h-10 px-3 text-sm font-mono text-white focus:border-secondary focus:outline-none disabled:opacity-50"
                >
                  {KARAOKE_BATCHES.map((b, i) => (
                    <option key={i} value={i} className="bg-surface text-white">
                      {`Batch ${i + 1} (${b.length} canciones)`}
                    </option>
                  ))}
                </select>
                <button
                  onClick={runBatch}
                  disabled={warming}
                  data-testid="load-batch-button"
                  className="shrink-0 h-10 px-4 rounded-md bg-secondary text-secondary-foreground font-bold text-xs uppercase tracking-wide hover:bg-secondary/90 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-1.5"
                >
                  {warming ? <Loader2 className="w-4 h-4 animate-spin" /> : <DownloadCloud className="w-4 h-4" />}
                  {warming ? "Cargando..." : "Cargar"}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground/60 font-mono mt-2">
                Costo máx. ~{((KARAOKE_BATCHES[batchIdx]?.length || 0) * 101).toLocaleString("es")} unidades · las canciones ya cacheadas se saltan (0 cuota).
              </p>

              {warming && (
                <div className="mt-3" data-testid="warm-progress">
                  <div className="flex justify-between text-[11px] font-mono text-muted-foreground mb-1">
                    <span>Procesando…</span>
                    <span>{progress.done}/{progress.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-secondary transition-all duration-300"
                      style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {warmReport && !warming && (
                <div className="mt-3 rounded-md border border-secondary/30 bg-secondary/[0.06] p-3" data-testid="warm-report">
                  <p className="text-xs font-bold text-secondary flex items-center gap-1.5 mb-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Batch {warmReport.batch} pre-cargado
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono text-muted-foreground">
                    <span>Cacheadas: <b className="text-foreground">{warmReport.cached}</b></span>
                    <span>Sin resultados: <b className="text-foreground">{warmReport.empty}</b></span>
                    <span>Fallidas: <b className="text-foreground">{warmReport.failed}</b></span>
                    <span>Videos inválidos: <b className="text-foreground">{warmReport.deleted + warmReport.priv}</b></span>
                    <span>Tiempo: <b className="text-foreground">{warmReport.elapsed}s</b></span>
                    <span>Cuota usada: <b className="text-foreground">{warmReport.quota.toLocaleString("es")}</b></span>
                  </div>
                  {warmReport.failed > 0 && (
                    <p className="text-[10px] text-muted-foreground/70 font-mono mt-2 leading-relaxed">
                      Los fallos suelen deberse al límite temporal de YouTube (cuota diaria o tasa).
                      Reintenta más tarde: las canciones ya cacheadas se conservan y no se reprocesan.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
    <CachedSongsDialog open={showCachedSongs} onOpenChange={setShowCachedSongs} onAddSong={onAddSong} onPlayNow={onPlayNow} />
    </>
  );
};

export default QuotaStatsDialog;
